import { Node, mergeAttributes } from '@tiptap/core'

/**
 * Available BE variables that get replaced server-side per-recipient.
 */
export const EMAIL_VARIABLES = [
  { id: 'name', label: 'Recipient Name' },
  { id: 'teamName', label: 'Team Name' },
  { id: 'facilitatorName', label: 'Facilitator Name' },
  { id: 'appURL', label: 'Sign-in Link' }
] as const

export type EmailVariableId = (typeof EMAIL_VARIABLES)[number]['id']

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variableChip: {
      insertVariable: (variableId: EmailVariableId) => ReturnType
    }
    activitiesBlock: {
      insertActivitiesBlock: () => ReturnType
    }
  }
}

/**
 * TipTap custom node extension for rendering BE variables as chips.
 * Serializes to {{variableName}} in HTML output.
 */
export const VariableChipExtension = Node.create({
  name: 'variableChip',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      variableId: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-variable-id'),
        renderHTML: (attributes) => ({
          'data-variable-id': attributes.variableId
        })
      }
    }
  },

  parseHTML() {
    return [
      {
        tag: 'span[data-variable-id]'
      }
    ]
  },

  renderHTML({ HTMLAttributes }) {
    const variableId = HTMLAttributes['data-variable-id'] as string
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        'data-variable-id': variableId,
        class: 'variable-chip',
        contenteditable: 'false',
        style:
          'display: inline-flex; align-items: center; border: 1px solid rgba(255,255,255,0.4); border-radius: 12px; padding: 1px 8px; font-size: 12px; font-weight: 500; user-select: none; vertical-align: baseline; margin: 0 2px;'
      }),
      `{{${variableId}}}`
    ]
  },

  addCommands() {
    return {
      insertVariable:
        (variableId: EmailVariableId) =>
        ({ commands }) => {
          return commands.insertContent({
            type: this.name,
            attrs: { variableId }
          })
        }
    }
  }
})

/**
 * TipTap custom block node for the activities schedule placeholder.
 * Renders as a read-only card in the editor. Serializes to {{ACTIVITIES_BLOCK}} in HTML.
 */
export const ActivitiesBlockExtension = Node.create({
  name: 'activitiesBlock',
  group: 'block',
  atom: true,
  draggable: false,
  selectable: true,

  parseHTML() {
    return [{ tag: 'div[data-activities-block]' }]
  },

  renderHTML() {
    return [
      'div',
      {
        'data-activities-block': 'true',
        contenteditable: 'false',
        style:
          'background: #1e3a5f; border: 1px solid #2d5a8e; border-radius: 8px; padding: 12px 16px; margin: 8px 0; user-select: none; display: flex; align-items: center; gap: 8px; color: #94a3b8; font-size: 13px;'
      },
      '\uD83D\uDCCB Activities Schedule (auto-generated)'
    ]
  },

  addCommands() {
    return {
      insertActivitiesBlock:
        () =>
        ({ commands }) => {
          return commands.insertContent({ type: this.name })
        }
    }
  }
})

/** Variable chip style in editor: no background so it never leaks into email. */
const VARIABLE_CHIP_STYLE =
  'display: inline-flex; align-items: center; border: 1px solid rgba(255,255,255,0.4); border-radius: 12px; padding: 1px 8px; font-size: 12px; font-weight: 500; user-select: none; vertical-align: baseline; margin: 0 2px;'

/**
 * Convert HTML with chip spans / activities block back to template syntax.
 * Uses .*? for chip content so nested or wrapped content is fully stripped (no styling sent).
 */
export function serializeEditorHtml(html: string): string {
  let result = html
  // Variable chips → {{variableId}} (match any content inside span so no chip style is sent)
  result = result.replace(/<span[^>]*data-variable-id="([^"]*)"[^>]*>.*?<\/span>/gs, '{{$1}}')
  // Activities block → {{ACTIVITIES_BLOCK}}
  result = result.replace(/<div[^>]*data-activities-block="true"[^>]*>[^<]*<\/div>/g, '{{ACTIVITIES_BLOCK}}')
  return result
}

/**
 * Convert template syntax back to editor HTML for loading into TipTap.
 */
export function deserializeToEditorHtml(html: string): string {
  let result = html
  // Variable placeholders → chip spans
  result = result.replace(
    /\{\{(name|teamName|facilitatorName|appURL)\}\}/g,
    (_match, variableId) =>
      `<span data-variable-id="${variableId}" class="variable-chip" contenteditable="false" style="${VARIABLE_CHIP_STYLE}">{{${variableId}}}</span>`
  )
  // Activities block placeholder → block div
  result = result.replace(
    /\{\{ACTIVITIES_BLOCK\}\}/g,
    '<div data-activities-block="true" contenteditable="false" style="background: #1e3a5f; border: 1px solid #2d5a8e; border-radius: 8px; padding: 12px 16px; margin: 8px 0; user-select: none; display: flex; align-items: center; gap: 8px; color: #94a3b8; font-size: 13px;">\uD83D\uDCCB Activities Schedule (auto-generated)</div>'
  )
  return result
}
